'use client'

import * as React from 'react'

import { ToggleGroup } from '@ark-ui/react'
import { useZero } from '@rocicorp/zero/react'
import {
  Controller,
  FormLayout,
  SubmitButton,
  createField,
  useForm,
} from '@saas-ui/forms'
import {
  Box,
  Button,
  ButtonGroup,
  Dialog,
  Text,
  Textarea,
  toast,
} from '@saas-ui/react'
import { LuCircleHelp, LuMessageCircle, LuTriangleAlert } from 'react-icons/lu'

import { useWorkspace } from '#features/common/hooks/use-workspace.ts'
import { useOpenState } from '#hooks/use-open-state.ts'
import { awaitServerMutation } from '#zero/await-server-mutation.ts'
import { mutators } from '#zero/mutators.ts'

export interface FeedbackInput {
  type: 'problem' | 'question' | 'feedback'
  comment: string
}

export interface FeedbackDialogProps extends Omit<
  Dialog.RootProps,
  'children'
> {}

export const FeedbackDialog: React.FC<FeedbackDialogProps> = (props) => {
  const { open, onOpenChange, ...rest } = props

  const workspace = useWorkspace()

  const openState = useOpenState({
    defaultOpen: open,
    onOpenChange: onOpenChange,
  })

  const zero = useZero()

  const form = useForm<FeedbackInput, object>({
    defaultValues: {
      type: 'feedback',
      comment: '',
    },
    onSubmit: async ({ type, comment }) => {
      try {
        await awaitServerMutation(
          zero.mutate(
            mutators.feedback.submit({
              type,
              comment,
              url: window.location.href,
              workspaceSlug: workspace || undefined,
              userAgent: navigator.userAgent,
              viewport: `${window.innerWidth}x${window.innerHeight}`,
            }),
          ),
        )
        toast.success({
          title: 'Thank you!',
          description: 'Your feedback has been submitted.',
        })
        openState.onOpenChange({ open: false })
        form.reset()
      } catch {
        toast.error({
          title: 'Failed to send feedback',
          description: 'Please try again later.',
        })
      }
    },
  })

  return (
    <Dialog.Root
      size="lg"
      open={openState.open}
      onOpenChange={openState.onOpenChange}
      {...rest}
    >
      <Dialog.Backdrop />
      <Dialog.Content>
        <form.Form>
          <Dialog.Header>
            <Box>
              <Dialog.Title>Feedback</Dialog.Title>
              <Dialog.Description>
                Got feedback? Share your feature requests and why they're
                important.
              </Dialog.Description>
            </Box>
            <Dialog.CloseButton />
          </Dialog.Header>
          <Dialog.Body>
            <FormLayout>
              <ToggleButtonField
                name="type"
                items={[
                  {
                    value: 'feedback',
                    label: 'Feedback',
                    icon: <LuMessageCircle />,
                  },
                  {
                    value: 'problem',
                    label: 'Problem',
                    icon: <LuTriangleAlert />,
                  },
                  {
                    value: 'question',
                    label: 'Question',
                    icon: <LuCircleHelp />,
                  },
                ]}
              />
              <Controller
                name="comment"
                control={form.control}
                render={({ field }) => (
                  <Textarea
                    border="0"
                    height="120px"
                    placeholder="What if..."
                    p="0"
                    bg="transparent"
                    boxShadow="none"
                    _focus={{
                      border: 0,
                    }}
                    _focusVisible={{
                      border: 0,
                    }}
                    {...field}
                  ></Textarea>
                )}
              />
            </FormLayout>
          </Dialog.Body>
          <Dialog.Footer gap="4" borderTopWidth="1px">
            <Text textStyle="sm" color="fg.muted">
              We're committed to reading all messages, even if we can't respond
              to every single one.
            </Text>
            <SubmitButton flexShrink="0">Send feedback</SubmitButton>
          </Dialog.Footer>
        </form.Form>
      </Dialog.Content>
    </Dialog.Root>
  )
}

interface ToggleButtonFieldProps extends Omit<
  ToggleGroup.RootProps,
  'value' | 'onChange'
> {
  items: {
    value: string
    label: string
    icon: React.ReactElement
  }[]
  value?: string
  onChange?: (value: string) => void
}

const ToggleButtonField = createField<HTMLDivElement, ToggleButtonFieldProps>(
  (props, ref) => {
    const { items, value: valueProp, onChange, ...rest } = props

    const value = React.useMemo(
      () => (valueProp ? [valueProp] : undefined),
      [valueProp],
    )

    return (
      <ToggleGroup.Root
        ref={ref}
        value={value}
        onValueChange={({ value }) => onChange(value[0])}
        multiple={false}
        asChild
      >
        <ButtonGroup attached={false} {...rest}>
          {items.map((item) => (
            <ToggleGroup.Item key={item.value} value={item.value} asChild>
              <Button
                variant="outline"
                size="sm"
                _checked={{
                  borderColor: 'accent.solid',
                  bg: 'accent.muted',
                  color: 'accent.solid',
                }}
              >
                {item.icon}
                {item.label}
              </Button>
            </ToggleGroup.Item>
          ))}
        </ButtonGroup>
      </ToggleGroup.Root>
    )
  },
  {
    isControlled: true,
  },
)
